'use client';

import { Loader2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import type {
  InstallationSyncStatus,
  InstallationSyncProgressData,
} from '@/types/installationSync';

interface InstallationSyncBannerProps {
  status: InstallationSyncStatus;
  progressData: InstallationSyncProgressData | null;
  onRetry?: () => void;
  onDismiss?: () => void;
}

export function InstallationSyncBanner({
  status,
  progressData,
  onRetry,
  onDismiss,
}: InstallationSyncBannerProps) {
  if (status === 'IN_PROGRESS') {
    const total = progressData?.total ?? 0;
    const processed = progressData?.processed ?? 0;
    const percent = total > 0 ? Math.round((processed / total) * 100) : 0;

    return (
      <div
        role="status"
        aria-live="polite"
        className="flex flex-col gap-2 rounded-lg border border-ds-border-subtlest bg-ds-brand-subtlest/10 px-4 py-3"
      >
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-ds-brand-default" />
          <span className="ds-text-body-sm-medium text-ds-text-darker">
            Syncing repositories...
          </span>
        </div>
        <Progress value={processed} max={total} className="h-2" />
        <p className="ds-text-body-xs-medium text-ds-text-subtler">
          {processed} of {total} processed
        </p>
      </div>
    );
  }

  if (status === 'FAILED') {
    const failedRepos = progressData?.failed_repos ?? [];

    return (
      <div
        role="alert"
        className="flex flex-col gap-3 rounded-lg border border-ds-danger-subtler bg-ds-danger-subtlest/30 px-4 py-3"
      >
        <p className="ds-text-body-sm-semibold text-ds-danger-default">
          Some repositories failed to sync
        </p>
        {failedRepos.length > 0 && (
          <ul className="ds-text-body-xs-medium text-ds-text-subtler list-inside list-disc space-y-0.5">
            {failedRepos.map((repo) => (
              <li key={repo}>{repo}</li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap gap-2">
          {onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry}>
              Retry
            </Button>
          )}
          {onDismiss && (
            <Button variant="secondary" size="sm" onClick={onDismiss}>
              Dismiss
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (status === 'COMPLETED' && onDismiss) {
    return (
      <div
        role="status"
        className="flex items-center justify-between gap-2 rounded-lg border border-ds-success-subtler bg-ds-success-subtler/30 px-4 py-2"
      >
        <p className="ds-text-body-sm-medium text-ds-success-default">
          Repositories synced successfully
        </p>
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
    );
  }

  return null;
}
